/**
 * Scoring for a paged lesson — one point per page, a short quiz after each.
 *
 * Kept separate from the component so it can be tested in plain node. The
 * numbers here decide whether someone passes a module, so they need to be
 * checkable without a browser.
 *
 * The output shape deliberately matches what Quiz.jsx writes. The admin
 * console, the per-question drill-down modal and the reissue flow all read
 * that shape, and none of them should have to know which kind of module
 * produced it.
 */

/** Credit decays with each attempt at the same question. Same as Quiz. */
export const ATTEMPT_SCORES = [1, 0.5, 0.25]
export const MAX_ATTEMPTS = ATTEMPT_SCORES.length

/**
 * Credit for getting a question right on attempt `n` (1-based).
 * Wrong after every attempt scores zero.
 */
export function scoreForAttempt(attempt) {
  if (!Number.isInteger(attempt) || attempt < 1 || attempt > MAX_ATTEMPTS) return 0
  return ATTEMPT_SCORES[attempt - 1]
}

/** True once a question is settled — either right, or out of attempts. */
export function isResolved(result) {
  return Boolean(result) && (result.correct === true || result.attempts >= MAX_ATTEMPTS)
}

/**
 * Flatten `pages` into one list of questions carrying their page index, so a
 * question can be scored globally while still knowing where it came from.
 */
export function flattenQuestions(pages = []) {
  const out = []
  pages.forEach((page, pageIndex) => {
    ;(page.questions || []).forEach((q) => {
      out.push({ ...q, pageIndex, questionIndex: out.length })
    })
  })
  return out
}

/** Indexes of the questions belonging to one page, in global numbering. */
export function questionIndexesForPage(pages, pageIndex) {
  return flattenQuestions(pages)
    .filter((q) => q.pageIndex === pageIndex)
    .map((q) => q.questionIndex)
}

/** Every question on this page is settled, so the reader may move on. */
export function isPageComplete(pages, pageIndex, results) {
  const idxs = questionIndexesForPage(pages, pageIndex)
  if (idxs.length === 0) return true // a page with no questions never blocks
  return idxs.every((i) => isResolved(results[i]))
}

/**
 * Build the record stored for one answered question. Carries the question
 * text and options so the admin drill-down can show what was actually asked
 * without re-deriving it from the module source, which may have changed.
 */
export function buildQuestionResult(question, { attempts, correct, timeMs, selectedAnswers }) {
  return {
    questionIndex: question.questionIndex,
    pageIndex: question.pageIndex,
    attempts,
    correct,
    score: correct ? scoreForAttempt(attempts) : 0,
    timeMs: timeMs || 0,
    selectedAnswers: selectedAnswers || [],
    questionText: question.question,
    options: question.options,
    correctIndex: question.correctIndex,
  }
}

/**
 * Check a lesson's questions are answerable before anyone is asked them.
 *
 * The failure this exists for: a `correctIndex` that points outside the
 * options, or at the wrong option. The first is detectable and this catches
 * it; the second isn't, which is why PagedLesson surfaces these on the page
 * rather than only in a test — a trainee who sees "the answer is highlighted
 * above" pointing at nothing needs to know it's our bug, not theirs.
 *
 * Returns [] when everything is well-formed.
 */
export function validateQuestions(pages = []) {
  const problems = []
  flattenQuestions(pages).forEach((q) => {
    const where = `question ${q.questionIndex + 1} (point ${q.pageIndex + 1})`
    if (!q.question || typeof q.question !== 'string') {
      problems.push(`${where}: no question text`)
    }
    if (!Array.isArray(q.options) || q.options.length < 2) {
      problems.push(`${where}: needs at least two options`)
      return // the index check below is meaningless without options
    }
    if (!Number.isInteger(q.correctIndex)) {
      problems.push(`${where}: correctIndex is not a whole number`)
    } else if (q.correctIndex < 0 || q.correctIndex >= q.options.length) {
      problems.push(
        `${where}: correctIndex ${q.correctIndex} is outside its ${q.options.length} options`
      )
    }
    const seen = new Set(q.options.map((o) => String(o).trim().toLowerCase()))
    if (seen.size !== q.options.length) {
      problems.push(`${where}: two options are identical`)
    }
  })
  return problems
}

/**
 * Aggregate a full run. `total` is the number of questions asked, which is
 * also the maximum score — every question is worth 1 at best.
 */
export function aggregateResults(results = [], total = 0, passingScore = 0.7) {
  const answered = results.filter(Boolean)
  const totalScore = answered.reduce((sum, r) => sum + (r?.score || 0), 0)
  const maxPossibleScore = total
  const percentageScore = maxPossibleScore > 0 ? totalScore / maxPossibleScore : 0
  return {
    totalScore,
    maxPossibleScore,
    percentageScore,
    passed: maxPossibleScore > 0 && percentageScore >= passingScore,
    correctCount: answered.filter((r) => r.correct).length,
    answeredCount: answered.length,
  }
}

/**
 * What a finished run should record.
 *
 * `storeScore` is the part that matters. A lesson with NO questions — module
 * 17, which hands its steps to the Confluence guides — still completes, but
 * must not store a score: aggregateResults returns 0 for an empty run, and a
 * stored 0 is indistinguishable from a real zero downstream (progressStats
 * counts it), so writing it would drop a nought into the trainee's average as
 * the reward for reading a page.
 */
export function recordForFinishedRun(results, total, passingScore = 0.7, { readThrough = false } = {}) {
  return {
    agg: aggregateResults(results, total, passingScore),
    storeScore: total > 0,
    // A read-through completes with no score. It must be DECLARED by the module
    // rather than inferred from having no questions, because the inference
    // fails open — a lesson that lost its questions would quietly record
    // evidence-free completions instead of failing loudly.
    unscored: total === 0 && readThrough,
    // Questions absent from a lesson that never claimed to be a read-through.
    // That is a fault in the course, not a kind of lesson.
    undeclared: total === 0 && !readThrough,
  }
}

/* ─── Reissue and attempt history ─── */

/**
 * Milliseconds for a value that may be a Firestore Timestamp, an ISO string,
 * a Date or a number. Returns 0 for anything unreadable, so a missing value
 * sorts before every real one.
 */
export function toMillis(value) {
  if (!value) return 0
  if (typeof value.toDate === 'function') return value.toDate().getTime()
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return value
  const t = new Date(value).getTime()
  return Number.isFinite(t) ? t : 0
}

/**
 * Has an admin reissued this module since the trainee last completed it?
 *
 * Pulled out of the components because it decides whether someone is shown a
 * fresh run or a stale "you're done" screen, and it was previously written
 * three times — once in Quiz against Firestore, once in PagedLesson against
 * context state, once in ProgressContext. The PagedLesson copy was the one
 * that silently did nothing on a cold page load.
 */
export function isReissuePending(progress) {
  if (!progress?.reissued) return false
  return toMillis(progress.reissued) > lastCompletedAt(progress)
}

/**
 * When this module was last finished, in milliseconds.
 *
 * The LATER of the two timestamps, not "quizResults if present". Both are
 * needed: a retake writes `quizResults.completedAt` while the document's own
 * completedAt may be older, and a read-through stores no quizResults at all,
 * so reading only that gave 0 — and a reissue compared against 0 is pending
 * forever, so the trainee would be sent back to the module every time they
 * finished it.
 *
 * Preferring quizResults instead of taking the max is what broke the reissue
 * of a module that keeps an old score. A reissue deletes `completed` and
 * `completedAt` but KEEPS `quizResults`, so a legacy scored completion of a
 * module that is now a read-through leaves a stale `quizResults.completedAt`
 * behind. The retake then writes only the document-level completedAt, which a
 * `||` fallback never reaches — the reissue stays pending, the finished run is
 * reset on every visit, and finishing any other module bounces the trainee
 * back here with no way out.
 */
export function lastCompletedAt(progress) {
  return Math.max(
    toMillis(progress?.quizResults?.completedAt),
    toMillis(progress?.completedAt)
  )
}

/**
 * Keep the most recent `max` attempts, renumbering them so `attempt` still
 * counts from 1 for whatever is retained.
 *
 * The history embeds every question's text, options and selected answers, so
 * an unlimited retake button grows the progress document a few KB per go.
 * Firestore refuses a document over 1 MiB — and once it refuses, EVERY later
 * write to that trainee's module, completion included, fails too.
 */
export function capAttemptHistory(history = [], max = 10) {
  const kept = history.slice(-Math.max(1, max))
  return kept.map((entry, i) => ({ ...entry, attempt: i + 1 }))
}

/**
 * May the trainee jump straight to page `target`?
 *
 * Backwards is always fine. Forwards requires every page BEFORE the target to
 * be settled — not, as this once read, the target page itself. Because a page
 * carrying no questions is complete by definition, the old test made any such
 * page reachable from anywhere: if it happened to be the last page, a trainee
 * on page 1 could jump to it, find "Finish module" enabled, and record the
 * module complete at 0% having answered nothing.
 */
export function canJumpToPage(pages = [], target, results = [], pageIndex = 0) {
  if (target <= pageIndex) return true
  for (let i = 0; i < target; i++) {
    if (!isPageComplete(pages, i, results)) return false
  }
  return true
}

